import { apiClient } from './api';

export interface VoiceSession {
  session_id: string;
  agent_id: string;
  module_id: string;
  context_type: string;
  context_reference_id: string;
  state: string;
  generation: number;
}

export interface VoiceAskResult {
  session_id: string;
  generation: number;
  ask: {
    question_id?: number;
    job_id?: string;
    status?: string;
    user_message?: string;
    answer?: string;
  };
}

export type VoiceActiveSlot = 'greening1' | 'greening2' | 'greening3';

export interface VoiceTtsAvailability {
  greening1: boolean;
  greening2: boolean;
  greening3: boolean;
}

/** `tts` = disponibilidade por avatar (vários true ok); `active` = hint padrão no servidor. */
export interface VoiceConfig {
  enabled: boolean;
  active: VoiceActiveSlot;
  tts: VoiceTtsAvailability;
}

export const voiceApi = {
  async config(): Promise<VoiceConfig> {
    const { data } = await apiClient.get('/api/greening/voice/config', { skipErrorToast: true });
    const rawActive = String(data?.active || 'greening1').toLowerCase();
    const active: VoiceActiveSlot =
      rawActive === 'greening2' || rawActive === 'greening3' ? rawActive : 'greening1';
    const rawTts = data?.tts && typeof data.tts === 'object' ? data.tts : {};
    return {
      enabled: Boolean(data?.enabled),
      active,
      tts: {
        greening1: Boolean(rawTts.greening1),
        greening2: Boolean(rawTts.greening2),
        greening3: Boolean(rawTts.greening3),
      },
    };
  },

  async openSession(input: {
    agentId: string;
    contextType: string;
    referenceId: string;
  }): Promise<VoiceSession> {
    const { data } = await apiClient.post('/api/greening/voice/sessions', {
      agent_id: input.agentId,
      context_type: input.contextType,
      context_reference_id: input.referenceId,
    });
    return data;
  },

  async transcribe(sessionId: string, audio: Blob, generation: number): Promise<{ text: string; generation: number }> {
    const { data } = await apiClient.post(`/api/greening/voice/sessions/${sessionId}/transcribe`, audio, {
      params: { generation },
      timeout: 90_000,
      headers: {
        'Content-Type': audio.type || 'audio/webm',
        'X-Audio-Filename': 'audio.webm',
      },
    });
    return data;
  },

  async ask(sessionId: string, question: string, generation: number): Promise<VoiceAskResult> {
    const { data } = await apiClient.post(`/api/greening/voice/sessions/${sessionId}/ask`, {
      question,
      generation,
    });
    return data;
  },

  async interrupt(sessionId: string): Promise<VoiceSession> {
    const { data } = await apiClient.post(`/api/greening/voice/sessions/${sessionId}/interrupt`);
    return data;
  },

  async close(sessionId: string): Promise<VoiceSession> {
    const { data } = await apiClient.post(`/api/greening/voice/sessions/${sessionId}/close`);
    return data;
  },

  async synthesize(path: string, text: string): Promise<Blob> {
    const { data } = await apiClient.post(
      path,
      { text },
      { timeout: 50_000, skipErrorToast: true },
    );
    const encoded = String(data?.audio_base64 || '');
    if (!encoded) throw new Error('tts_empty');
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: data?.content_type || 'audio/mpeg' });
  },
};
